import {
  ArrowsRightLeftIcon,
  Bars3BottomLeftIcon,
  ChartBarSquareIcon,
  ClipboardDocumentListIcon,
  Squares2X2Icon,
} from "@heroicons/react/24/outline";
import { FC, useEffect, useMemo, useState } from "react";
import { PriceChart } from "./PriceChart";
import { MarketStats } from "./MarketStats";
import { MarketOverviewBar } from "./MarketOverviewBar";
import { OrderBook } from "./OrderBook";
import { SwapPanel } from "./SwapPanel";
import { ActivityPanel } from "./ActivityPanel";
import { TokenLoreCard } from "./TokenLoreCard";
import { AvailableBalance } from "./AvailableBalance";
import { NewsTicker } from "./NewsTicker";

import { useGummiwormContext } from "@/context/GummiwormContext";
import { cn } from "@/lib/utils";
import { useTradingViewState } from "./useTradingViewState";
import { useTradingHotkeys } from "./useTradingHotkeys";
import { HotkeyCheatsheet } from "./HotkeyCheatsheet";

type MobilePrimaryTab = "market" | "stats" | "book" | "orders" | "trade";

interface TradingViewProps {
  onDeposit: (assetId?: string) => void;
  depositDisabled?: boolean;
  revealWidgets?: boolean;
  /** Market to switch to when set (e.g. from a news headline link). */
  requestedMarketKey?: string | null;
}

const mobileTabs = [
  ["market", "Market", ChartBarSquareIcon],
  ["book", "Book", Bars3BottomLeftIcon],
  ["trade", "Trade", ArrowsRightLeftIcon],
  ["orders", "Orders", ClipboardDocumentListIcon],
  ["stats", "Stats", Squares2X2Icon],
] as const satisfies readonly [MobilePrimaryTab, string, typeof ArrowsRightLeftIcon][];

export const TradingView: FC<TradingViewProps> = ({
  onDeposit,
  depositDisabled,
  revealWidgets,
  requestedMarketKey,
}) => {
  const [mobileTab, setMobileTab] = useState<MobilePrimaryTab>("market");
  const { markets, watchMarket } = useGummiwormContext();
  const {
    selectedMarket,
    handleSelectMarket,
    tradeSide,
    setTradeSide,
    orderType,
    setOrderType,
    limitPrice,
    setLimitPrice,
    handleSelectPrice,
  } = useTradingViewState(markets, requestedMarketKey);

  // Scope the live orderbook + candle feed to the market on screen, so a page
  // load subscribes to ~7 channels instead of every market's.
  useEffect(() => {
    watchMarket(selectedMarket?.key ?? null);
  }, [selectedMarket?.key, watchMarket]);

  const { hotkeysEnabled, setHotkeysEnabled, cheatsheetOpen, setCheatsheetOpen } =
    useTradingHotkeys({
      markets,
      selectMarket: handleSelectMarket,
      setTradeSide,
      setOrderType,
      setLimitPrice,
    });

  const swapProps = {
    selectedMarket,
    tradeSide,
    onTradeSideChange: setTradeSide,
    orderType,
    onOrderTypeChange: setOrderType,
    limitPrice,
    onLimitPriceChange: setLimitPrice,
    onDeposit,
    depositDisabled,
  };
  const swapPanelKey = `${selectedMarket?.key ?? "none"}:${tradeSide}`;

  // Follow a market request (news headline link, etc.).
  useEffect(() => {
    if (!requestedMarketKey) return;
    const market = markets.find((m) => m.key === requestedMarketKey);
    if (market) handleSelectMarket(market);
  }, [requestedMarketKey, markets, handleSelectMarket]);

  const tradeToMarket = (marketKey: string) => {
    const market = markets.find((m) => m.key === marketKey);
    if (market) handleSelectMarket(market);
  };

  // Helper for staggered reveal animation.
  // Base delay accounts for the overlay fade (widgets render hidden behind it).
  const REVEAL_BASE_DELAY = 800;
  const rc = (delay: number, extra = "") =>
    revealWidgets
      ? `sr-reveal-widget ${extra}`.trim()
      : extra;
  const rs = (delay: number) =>
    revealWidgets ? { animationDelay: `${REVEAL_BASE_DELAY + delay}ms` } : undefined;

  const mobileContent = useMemo(() => {
    switch (mobileTab) {
      case "market":
        return (
          <PriceChart
            immersiveMobile
            selectedMarket={selectedMarket}
          />
        );
      case "stats":
        return (
          <div className="flex flex-col gap-1">
            <MarketStats
              selectedMarket={selectedMarket}
              columnsClassName="grid-cols-1 sm:grid-cols-2"
              itemClassName="min-h-[84px]"
            />
            <TokenLoreCard selectedMarket={selectedMarket} />
          </div>
        );
      case "book":
        return <OrderBook selectedMarket={selectedMarket} onSelectPrice={handleSelectPrice} />;
      case "orders":
        return <ActivityPanel selectedMarket={selectedMarket} />;
      case "trade":
        return (
          <div className="flex flex-col gap-1">
            <SwapPanel key={swapPanelKey} {...swapProps} />
            <AvailableBalance />
          </div>
        );
    }
  }, [handleSelectPrice, mobileTab, selectedMarket, swapPanelKey, swapProps]);

  return (
    <div className="flex min-h-0 w-full flex-1 flex-col gap-1">
      <HotkeyCheatsheet
        open={cheatsheetOpen}
        onClose={() => setCheatsheetOpen(false)}
        hotkeysEnabled={hotkeysEnabled}
        onToggleHotkeys={setHotkeysEnabled}
      />
      {/* Competition news wire — full width, both layouts */}
      <div className={rc(100)} style={rs(100)}>
        <NewsTicker onTradeMarket={tradeToMarket} />
      </div>

      {/* === Desktop Layout === */}
      <div className="hidden min-h-0 flex-1 gap-1 lg:grid lg:grid-cols-[420px_minmax(0,1fr)]">
        {/* Left column: Swap + Balance */}
        <div className="flex min-w-0 flex-col gap-1">
          <div className={rc(0)} style={rs(0)}><SwapPanel key={swapPanelKey} {...swapProps} /></div>
          <div className={rc(400)} style={rs(400)}><AvailableBalance /></div>
        </div>

        {/* Right column: Chart + Book + Activity */}
        <div className="flex min-h-0 min-w-0 flex-col gap-1">
          <div className={rc(120)} style={rs(120)}>
            <MarketOverviewBar
              compact
              selectedMarket={selectedMarket}
              markets={markets}
              onSelectMarket={handleSelectMarket}
            />
          </div>
          {/* Chart + book take the remaining height (flex-1) rather than a
              hard-coded 100vh-280px reserve, which clipped the orders row when
              the market bar rendered taller than the reserve assumed. */}
          <div className="grid min-h-[360px] min-w-0 flex-1 gap-1 lg:grid-cols-[minmax(0,1fr)_300px]">
            <div className="flex min-h-0 min-w-0 flex-col">
              <div className={rc(280, "min-h-0 h-full")} style={rs(280)}>
                <PriceChart selectedMarket={selectedMarket} />
              </div>
            </div>
            <div className={rc(280, "min-h-0 overflow-hidden")} style={rs(280)}>
              <OrderBook selectedMarket={selectedMarket} onSelectPrice={handleSelectPrice} />
            </div>
          </div>
          {/* Orders + lore get a bounded height and scroll internally, so they
              are always visible under the chart rather than pushed off-screen. */}
          <div className="grid min-w-0 shrink-0 gap-1 lg:h-[300px] lg:grid-cols-[minmax(0,1fr)_minmax(0,1fr)]">
            <div className={rc(700, "min-h-0")} style={rs(700)}><ActivityPanel selectedMarket={selectedMarket} /></div>
            <div className={rc(750, "min-h-0")} style={rs(750)}><TokenLoreCard selectedMarket={selectedMarket} /></div>
          </div>
        </div>
      </div>

      {/* === Mobile Layout === */}
      <div className="relative flex flex-col gap-1 pb-32 lg:hidden">
        <div className="sticky top-0 z-20 flex flex-col gap-1 bg-sr-espresso/90 pb-1 backdrop-blur">
          <MarketOverviewBar
            compact
            selectedMarket={selectedMarket}
            markets={markets}
            onSelectMarket={handleSelectMarket}
          />
          <NewsTicker onTradeMarket={tradeToMarket} />
        </div>

        <div role="tabpanel" id={`mobile-panel-${mobileTab}`} aria-labelledby={`mobile-tab-${mobileTab}`}>
          {mobileContent}
        </div>

        <div
          className="pointer-events-none fixed inset-x-0 bottom-0 z-30 flex justify-center px-4"
          style={{ paddingBottom: "max(0.75rem, calc(env(safe-area-inset-bottom) + 0.5rem))" }}
        >
          <div
            className="sr-floating-dock pointer-events-auto relative flex w-full max-w-md items-stretch gap-1 p-1.5"
            role="tablist"
            aria-label="Mobile trading navigation"
          >
            {mobileTabs.map(([key, label, Icon]) => (
              <button
                key={key}
                id={`mobile-tab-${key}`}
                role="tab"
                aria-selected={mobileTab === key}
                aria-controls={`mobile-panel-${key}`}
                onClick={() => setMobileTab(key)}
                className={cn(
                  "sr-focus-ring relative flex flex-1 flex-col items-center justify-center gap-1 rounded-[22px] px-2 py-2.5 text-[11px] font-medium transition-colors",
                  mobileTab === key
                    ? "bg-sr-espresso text-sr-lemon-muted"
                    : "text-sr-neutral-500 hover:text-sr-neutral-300",
                )}
              >
                <Icon className="h-4 w-4" />
                {label}
              </button>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
};
